import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';

import useProductCardInview from '@/tracks/hooks/useProductCardImpresion';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { isMobile } from '@/utils/common/device';
import { buildUrlWithOrigin, convertObjectToUrlParams } from '@/utils/url';
import { isSayWeeeApp } from '@/utils/weeeapp';
import { twMerge } from 'tailwind-merge';
import { showLoading } from '../Indicator/pageIndicator';
import type { ProductCardProps } from './types';
import { getPageKey } from '@/tracks/getPageKey';

const ProductContainer: React.FC<Partial<ProductCardProps>> = props => {
  const {
    data,
    isLink = true,
    link,
    skeleton,
    skeletonFactor = false,
    className,
    addCartParams,
    children,
    transformLink,
    isInUpsell = false,
    miniTitle = false,
    cardSize,
    linkParams,
    showLoadingWhenJump,
    forceReload
  } = props;
  const { lang } = useTranslation('common');
  const router = useRouter();
  const adsCreative = data?.ads_creative || undefined;

  const { ref, clickOption, impressionOption } = useProductCardInview(props);
  const { productClick } = useClickActionT2(adsCreative);

  const productLink = useMemo(() => {
    /**
     * 如果是广告商品，跳转链接使用trackRedirectUrl
     */
    if (data.is_sponsored && data.ads_creative) {
      const paramsString = data.ads_creative?.trackRedirectUrl?.split('?')?.[1];
      const paramsObj = new URLSearchParams(`?${paramsString}`);
      const adsUrlParams = Object.fromEntries(paramsObj.entries());

      return {
        to: buildUrlWithOrigin(
          `/${lang}/product/${data?.slug ? data?.slug : `weee/${data?.id}`}` +
            convertObjectToUrlParams({ ...linkParams, ...adsUrlParams })
        ),
        reload: false
      };
    }
    // 强制跳转刷新
    if (forceReload) {
      return {
        to: buildUrlWithOrigin(
          `/${lang}/product/${data?.slug ? data?.slug : `weee/${data?.id}`}` + convertObjectToUrlParams(linkParams)
        ),
        reload: true
      };
    }
    let reload = false;
    if (isSayWeeeApp() || !isMobile()) {
      reload = true;
    }
    if (link) {
      return {
        to: link,
        reload: reload
      };
    }
    if (isInUpsell) {
      return {
        to: 'upsell',
        reload: false
      };
    }
    //bundle
    if (data?.category === 'bundle') {
      return {
        to: buildUrlWithOrigin(`/${lang}/bundle_buy/product?product_id=${data?.id}`),
        reload: true
      };
    }
    if (transformLink) {
      const _transformLink = transformLink(data);
      //处理transformLink返回的是对象的情况
      if (typeof _transformLink === 'object') {
        return {
          to: _transformLink.to,
          reload: _transformLink.reload
        };
      }
      return {
        to: _transformLink,
        reload: reload
      };
    }
    return {
      to: buildUrlWithOrigin(
        `/${lang}/product/${data?.slug ? data?.slug : `weee/${data?.id}`}` + convertObjectToUrlParams(linkParams)
      ),
      reload: reload
    };
  }, [data, addCartParams?.source, lang, JSON.stringify(linkParams), isInUpsell]);

  const pageKey = useMemo(() => {
    if (typeof window === 'undefined') return ''; // SSR时返回空值
    return getPageKey(router.asPath);
  }, [router.asPath]);
  // 从 positionInfoT2 中获取 mod_nm
  const modNm = props?.positionInfoT2?.modSecPos?.mod_nm;

  const cls = twMerge(
    classNames('w-full flex flex-col justify-between relative bg-surface-100-bg overflow-hidden rounded-400', {
      'cursor-pointer': isLink,
      [className]: !!className,
      'h-full': miniTitle,
      'w-[150px]': cardSize === 'sm'
    })
  );

  const trackClick = useCallback(() => {
    if (showLoadingWhenJump) {
      showLoading();
    }
    productClick(clickOption);
    props?.afterTrackClick?.();
  }, [impressionOption, clickOption]);

  if (skeleton && skeletonFactor) {
    return <>{skeleton}</>;
  }

  if (productLink.to === 'upsell' || !isLink) {
    return (
      <div
        data-testid="wid-product-card-container"
        data-product-id={data?.id}
        data-trace-id={data?.recommend_trace_id || data?.recommendation_trace_id}
        data-mod-nm={modNm}
        data-page-key={pageKey ?? ''}
        className={cls}
        ref={ref}>
        {children}
      </div>
    );
  }

  if (productLink.reload) {
    return (
      <a
        data-testid="wid-product-card-container"
        data-product-id={data?.id}
        data-trace-id={data?.recommend_trace_id || data?.recommendation_trace_id}
        data-mod-nm={modNm}
        data-page-key={pageKey ?? ''}
        className={cls}
        href={productLink.to}
        ref={ref}
        onClick={trackClick}
        aria-label={`${data?.name}`}>
        {children}
      </a>
    );
  }

  return (
    <Link prefetch={false} href={productLink.to}>
      <a
        data-testid="wid-product-card-container"
        data-product-id={data?.id}
        data-trace-id={data?.recommend_trace_id || data?.recommendation_trace_id}
        data-mod-nm={modNm}
        data-page-key={pageKey ?? ''}
        className={cls}
        ref={ref}
        onClick={trackClick}
        aria-label={`weee ${data?.name} ${data?.id}`}>
        {children}
      </a>
    </Link>
  );
};

export default ProductContainer;
